import {createCWorld} from './c-world.js';
import {BRAND_STROKES} from './ink-brand.js';

const clamp = value => Math.max(0, Math.min(1, value));
const smooth = (a, b, value) => {const p = clamp((value - a) / (b - a)); return p * p * (3 - 2 * p);};
const mix = (a, b, p) => a + (b - a) * p;

// An intact official wordmark, rendered as a single, complete surface. This C
// identity has no writing order, stroke clock, draw range, dot or activation line.
function completeWordmark(T, scene) {
  const canvas=document.createElement('canvas');canvas.width=1680;canvas.height=780;
  const ctx=canvas.getContext('2d');ctx.scale(2,2);ctx.fillStyle='#fff';
  for(const stroke of BRAND_STROKES)ctx.fill(new Path2D(stroke.outline));
  const map=new T.CanvasTexture(canvas);map.colorSpace=T.NoColorSpace;
  const uniforms={image:{value:map},opacity:{value:.62},blur:{value:.0015},color:{value:new T.Color('#efe4c9')}};
  const material=new T.ShaderMaterial({uniforms,transparent:true,depthWrite:false,depthTest:false,
    vertexShader:'varying vec2 vUv;void main(){vUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);}',
    fragmentShader:`uniform sampler2D image;uniform float opacity,blur;uniform vec3 color;varying vec2 vUv;void main(){
      float a=texture2D(image,vUv).a*.40;
      a+=(texture2D(image,vUv+vec2(blur,0.)).a+texture2D(image,vUv-vec2(blur,0.)).a+texture2D(image,vUv+vec2(0.,blur)).a+texture2D(image,vUv-vec2(0.,blur)).a)*.15;
      gl_FragColor=vec4(color,a*opacity);
      #include <tonemapping_fragment>
      #include <colorspace_fragment>
    }`});
  const geometry=new T.PlaneGeometry(1,1),mesh=new T.Mesh(geometry,material);mesh.renderOrder=100;mesh.frustumCulled=false;scene.add(mesh);
  return {mesh,uniforms,dispose(){scene.remove(mesh);geometry.dispose();material.dispose();map.dispose();}};
}

export function createC({THREE:T,scene,camera,renderer,portrait=false}) {
  const world=createCWorld({THREE:T,scene,portrait}),brand=completeWordmark(T,scene);
  const duration=7.2,orbitEnd=3.9,arrivalEnd=6.10;
  let isPortrait=!!portrait,time=0,curve,disposed=false,roll=0,azimuth=-46;
  const target=new T.Vector3(),forward=new T.Vector3(),up=new T.Vector3();
  const finalPosition=new T.Vector3(.45,2.45,4.8),finalTarget=new T.Vector3(0,2.6,-7.4);
  function orbitPosition(progress) {
    // Establish the whole floating set from above its outer envelope. The
    // former start sat at the same radius as a distant pavilion's timber deck.
    // This added clearance disappears before the already-reviewed middle shot.
    const clearance=1-smooth(0,.65,progress);
    const angle=T.MathUtils.degToRad(mix(-46,62,progress)),radius=mix(isPortrait?34:30,isPortrait?25:21,progress)+12*clearance;
    const height=mix(isPortrait?11:9.8,isPortrait?8.7:7.4,progress)+Math.sin(progress*Math.PI)*1.4+(isPortrait?10:9.7)*clearance;
    return new T.Vector3(Math.sin(angle)*radius,height,Math.cos(angle)*radius);
  }
  function compose() {
    curve=new T.CatmullRomCurve3([orbitPosition(1),new T.Vector3(12.8,6.6,16.4),new T.Vector3(4.5,4.4,14.5),new T.Vector3(.65,2.85,10),finalPosition.clone()],false,'centripetal');
    curve.arcLengthDivisions=320;curve.updateArcLengths();
  }
  function update(seconds) {
    if(disposed)return;
    time=Math.max(0,Math.min(duration,Number.isFinite(seconds)?seconds:0));
    world.update(time);
    const orbit=smooth(.06,orbitEnd,time),arrival=smooth(orbitEnd,arrivalEnd,time);
    azimuth=mix(-46,62,orbit);
    if(time<=orbitEnd) {
      camera.position.copy(orbitPosition(orbit));target.set(0,mix(3.2,3.0,orbit),-1);
      roll=-Math.sin(orbit*Math.PI)*T.MathUtils.degToRad(isPortrait?23:28);
    } else {
      camera.position.copy(curve.getPointAt(arrival));target.set(0,3,-1).lerp(finalTarget,smooth(.0,.80,arrival));
      roll=Math.sin(arrival*Math.PI)*T.MathUtils.degToRad(isPortrait?5:8);
    }
    if(time>=arrivalEnd){camera.position.copy(finalPosition);target.copy(finalTarget);roll=0;}
    camera.up.set(0,1,0);camera.lookAt(target);camera.rotateZ(roll);
    const fov=time<=orbitEnd?mix(isPortrait?66:49,isPortrait?68:54,orbit):mix(isPortrait?68:54,isPortrait?64:52,arrival)+Math.sin(arrival*Math.PI)*3;
    if(camera.fov!==fov){camera.fov=fov;camera.updateProjectionMatrix();}
    renderer.toneMappingExposure=mix(.86,1.04,smooth(.3,5.65,time));
    // The complete identity softly settles into focus, then yields to the
    // orbiting architecture. It never starts from a point or a partial glyph.
    brand.uniforms.opacity.value=(.62+.38*smooth(0,.38,time))*(1-smooth(1.08,1.90,time));
    brand.uniforms.blur.value=mix(.0015,0,smooth(0,.55,time));
    brand.mesh.visible=brand.uniforms.opacity.value>.001;
    const distance=4.2,visibleHeight=2*Math.tan(T.MathUtils.degToRad(camera.fov)/2)*distance;
    const width=visibleHeight*camera.aspect*(isPortrait?.84:.46);
    camera.getWorldDirection(forward);up.set(0,1,0).applyQuaternion(camera.quaternion);
    brand.mesh.position.copy(camera.position).addScaledVector(forward,distance).addScaledVector(up,visibleHeight*.055);
    brand.mesh.quaternion.copy(camera.quaternion);brand.mesh.scale.set(width,width*390/840,1);
  }
  compose();update(0);
  return {duration,dynamicShadows:true,endcardTone:'dark',update,
    resize(value){isPortrait=!!value;compose();update(time);},
    inspect(){return {variant:'C',title:'浮殿・光之迴廊',time,portrait:isPortrait,phase:time<1.4?'完整字標・浮殿初見':time<3.9?'旋轉院落・多軸折景':time<6.1?'掠橋・穿門・抵達中庭':'品牌定格',identity:'complete original V23 wordmark, whole-form opacity / focus only',camera:{position:camera.position.toArray(),target:target.toArray(),fov:camera.fov,orbitDegrees:+azimuth.toFixed(2),rollDegrees:+T.MathUtils.radToDeg(roll).toFixed(2)},architecture:world.inspect()};},
    dispose(){if(disposed)return;disposed=true;brand.dispose();world.dispose();}
  };
}
